import React, { useState, useEffect, useRef } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../stores/authStore';
import { AdminPageHeader } from '../components/AdminPageHeader';

interface MaskedConfig {
  tenantId: string;
  persona: {
    provider: string;
    model: string;
    temperature: number;
  };
  evaluation: {
    provider: string;
    model: string;
    temperature: number;
  };
  stt: {
    provider: string;
    language: string;
  };
  tts: {
    provider: string;
    voiceId: string;
    language: string;
  };
  keysConfigured: {
    openai: boolean;
    openaiMasked?: string;
    gemini: boolean;
    geminiMasked?: string;
    anthropic: boolean;
    anthropicMasked?: string;
    googleCloud: boolean;
    googleCloudMasked?: string;
    ollamaBaseUrl?: string;
  };
}

interface ServiceHealth {
  status: 'idle' | 'testing' | 'active' | 'degraded' | 'offline';
  latencyMs?: number;
  message?: string;
  testedAt?: string;
}

export function TenantLLMSettingsPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'PLATFORM_ADMIN' || user?.role === 'SUPER_ADMIN';

  const [tenantsList, setTenantsList] = useState<Array<{ id: string; name: string; slug: string }>>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>(user?.tenantId || '');

  const [config, setConfig] = useState<MaskedConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Health states for each AI service & key
  const [health, setHealth] = useState<Record<string, ServiceHealth>>({
    persona: { status: 'idle' },
    eval: { status: 'idle' },
    stt: { status: 'idle' },
    tts: { status: 'idle' },
    openai: { status: 'idle' },
    gemini: { status: 'idle' },
    anthropic: { status: 'idle' },
    ollama: { status: 'idle' },
  });
  const [isPingingAll, setIsPingingAll] = useState(false);

  // Audio Preview State
  const [previewText, setPreviewText] = useState('Hello! This is a live voice preview from the Trainova AI platform.');
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Form State
  const [personaProvider, setPersonaProvider] = useState('openai');
  const [personaModel, setPersonaModel] = useState('gpt-4o-mini');
  const [personaTemperature, setPersonaTemperature] = useState(0.7);

  const [evaluationProvider, setEvaluationProvider] = useState('openai');
  const [evaluationModel, setEvaluationModel] = useState('gpt-4o-mini');
  const [evaluationTemperature, setEvaluationTemperature] = useState(0.2);

  const [sttProvider, setSttProvider] = useState('google');
  const [sttLanguage, setSttLanguage] = useState('en-IN');

  const [ttsProvider, setTtsProvider] = useState('google');
  const [ttsVoiceId, setTtsVoiceId] = useState('en-IN-Standard-A');
  const [ttsLanguage, setTtsLanguage] = useState('en-IN');

  const [openaiKey, setOpenaiKey] = useState('');
  const [geminiKey, setGeminiKey] = useState('');
  const [anthropicKey, setAnthropicKey] = useState('');
  const [googleKey, setGoogleKey] = useState('');
  const [ollamaUrl, setOllamaUrl] = useState('');

  // Load Tenants for Super Admin
  useEffect(() => {
    if (isSuperAdmin) {
      api.admin.tenants.list().then((res: any) => {
        if (res?.success && Array.isArray(res.data?.tenants)) {
          setTenantsList(res.data.tenants);
          if (!selectedTenantId && res.data.tenants.length > 0) {
            setSelectedTenantId(res.data.tenants[0].id);
          }
        }
      }).catch(err => console.warn('Could not load tenants list:', err));
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    fetchConfig();
  }, [selectedTenantId]);

  async function fetchConfig() {
    try {
      setIsLoading(true);
      const url = selectedTenantId
        ? `/api/v1/admin/llm-config?tenantId=${encodeURIComponent(selectedTenantId)}`
        : '/api/v1/admin/llm-config';
      const res = await api.get<MaskedConfig>(url);
      if (res.data) {
        setConfig(res.data);
        setPersonaProvider(res.data.persona.provider || 'openai');
        setPersonaModel(res.data.persona.model || 'gpt-4o-mini');
        setPersonaTemperature(res.data.persona.temperature ?? 0.7);

        setEvaluationProvider(res.data.evaluation.provider || 'openai');
        setEvaluationModel(res.data.evaluation.model || 'gpt-4o-mini');
        setEvaluationTemperature(res.data.evaluation.temperature ?? 0.2);

        setSttProvider(res.data.stt?.provider || 'google');
        setSttLanguage(res.data.stt?.language || 'en-IN');

        setTtsProvider(res.data.tts?.provider || 'google');
        setTtsVoiceId(res.data.tts?.voiceId || 'en-IN-Standard-A');
        setTtsLanguage(res.data.tts?.language || 'en-IN');

        setOllamaUrl(res.data.keysConfigured?.ollamaBaseUrl || 'http://localhost:11434');
      }
    } catch (err: any) {
      setFeedbackMessage({ type: 'error', text: err.message || 'Failed to load LLM configuration.' });
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    try {
      setIsSaving(true);
      setFeedbackMessage(null);
      const payload: any = {
        tenantId: selectedTenantId || undefined,
        personaProvider,
        personaModel,
        personaTemperature: Number(personaTemperature),
        evaluationProvider,
        evaluationModel,
        evaluationTemperature: Number(evaluationTemperature),
        sttProvider,
        sttLanguage,
        ttsProvider,
        ttsVoiceId,
        ttsLanguage,
        apiKeys: {},
      };

      if (openaiKey.trim()) payload.apiKeys.openai = openaiKey.trim();
      if (geminiKey.trim()) payload.apiKeys.gemini = geminiKey.trim();
      if (anthropicKey.trim()) payload.apiKeys.anthropic = anthropicKey.trim();
      if (googleKey.trim()) payload.apiKeys.googleCloud = googleKey.trim();
      if (ollamaUrl.trim()) payload.apiKeys.ollamaBaseUrl = ollamaUrl.trim();

      const url = selectedTenantId
        ? `/api/v1/admin/llm-config?tenantId=${encodeURIComponent(selectedTenantId)}`
        : '/api/v1/admin/llm-config';
      await api.post(url, payload);
      setFeedbackMessage({ type: 'success', text: 'AI and LLM provider configuration saved successfully.' });
      setOpenaiKey('');
      setGeminiKey('');
      setAnthropicKey('');
      setGoogleKey('');
      await fetchConfig();
    } catch (err: any) {
      setFeedbackMessage({ type: 'error', text: err.message || 'Failed to update LLM configuration.' });
    } finally {
      setIsSaving(false);
    }
  }

  async function testService(serviceKey: string) {
    setHealth((prev) => ({
      ...prev,
      [serviceKey]: { status: 'testing', message: 'Pinging service endpoint…' },
    }));

    const startTime = Date.now();
    try {
      let res: any;
      if (serviceKey === 'persona') {
        const key = personaProvider === 'openai' ? openaiKey : personaProvider === 'gemini' ? googleKey : personaProvider === 'anthropic' ? anthropicKey : undefined;
        res = await api.post('/api/v1/admin/test-llm-connection', {
          tenantId: selectedTenantId || undefined,
          provider: personaProvider,
          model: personaModel,
          apiKey: key || undefined,
          type: 'persona',
        });
      } else if (serviceKey === 'eval') {
        const key = evaluationProvider === 'openai' ? openaiKey : evaluationProvider === 'gemini' ? googleKey : evaluationProvider === 'anthropic' ? anthropicKey : undefined;
        res = await api.post('/api/v1/admin/test-llm-connection', {
          provider: evaluationProvider,
          model: evaluationModel,
          apiKey: key || undefined,
          type: 'eval',
        });
      } else if (serviceKey === 'stt') {
        res = await api.post('/api/v1/admin/test-stt-connection', {
          provider: sttProvider,
          language: sttLanguage,
          apiKey: googleKey || undefined,
        });
      } else if (serviceKey === 'tts') {
        res = await api.post('/api/v1/admin/test-tts-connection', {
          provider: ttsProvider,
          voiceId: ttsVoiceId,
          apiKey: googleKey || undefined,
        });
      } else if (serviceKey === 'openai') {
        res = await api.post('/api/v1/admin/test-llm-connection', {
          provider: 'openai',
          model: 'gpt-4o-mini',
          apiKey: openaiKey || undefined,
        });
      } else if (serviceKey === 'gemini') {
        res = await api.post('/api/v1/admin/test-llm-connection', {
          provider: 'gemini',
          model: 'gemini-1.5-flash',
          apiKey: googleKey || undefined,
        });
      } else if (serviceKey === 'anthropic') {
        res = await api.post('/api/v1/admin/test-llm-connection', {
          provider: 'anthropic',
          model: 'claude-3-5-haiku-20241022',
          apiKey: anthropicKey || undefined,
        });
      } else if (serviceKey === 'ollama') {
        res = await api.post('/api/v1/admin/test-llm-connection', {
          provider: 'ollama',
          baseUrl: ollamaUrl || undefined,
        });
      }

      const latencyMs = res?.latencyMs ?? (Date.now() - startTime);
      const isSuccess = Boolean(res?.success);
      const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

      setHealth((prev) => ({
        ...prev,
        [serviceKey]: {
          status: isSuccess ? 'active' : 'offline',
          latencyMs,
          message: res?.message || (isSuccess ? 'Service is operational and reachable.' : 'Connection failed.'),
          testedAt: timestamp,
        },
      }));
      return isSuccess;
    } catch (err: any) {
      const latencyMs = Date.now() - startTime;
      const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      setHealth((prev) => ({
        ...prev,
        [serviceKey]: {
          status: 'offline',
          latencyMs,
          message: err?.response?.data?.message || err?.message || 'Connection error / service unreachable.',
          testedAt: timestamp,
        },
      }));
      return false;
    }
  }

  async function pingAllServices() {
    setIsPingingAll(true);
    setFeedbackMessage(null);
    try {
      const servicesToPing = ['persona', 'eval', 'stt', 'tts'];
      await Promise.allSettled(servicesToPing.map((s) => testService(s)));
      setFeedbackMessage({
        type: 'success',
        text: 'Diagnostics completed: all configured AI services have been tested.',
      });
    } catch (err: any) {
      setFeedbackMessage({
        type: 'error',
        text: 'Error occurred during service health ping sweep.',
      });
    } finally {
      setIsPingingAll(false);
    }
  }

  async function handlePlayPreview() {
    try {
      setIsPlayingAudio(true);
      setAudioError(null);
      const res: any = await api.post('/api/v1/admin/synthesize-preview', {
        provider: ttsProvider,
        voiceId: ttsVoiceId,
        language: ttsLanguage,
        text: previewText.trim() || 'Hello from Trainova AI Learner Portal.',
        apiKey: googleKey || undefined,
      });

      if (res?.success && res?.audioBase64) {
        const mimeType = res.mimeType || 'audio/mp3';
        const audioSrc = `data:${mimeType};base64,${res.audioBase64}`;
        if (audioRef.current) {
          audioRef.current.src = audioSrc;
          await audioRef.current.play();
        } else {
          const audio = new Audio(audioSrc);
          audioRef.current = audio;
          await audio.play();
        }
      } else {
        setAudioError(res?.message || 'Synthesis returned no audio data.');
      }
    } catch (err: any) {
      setAudioError(err?.response?.data?.message || err?.message || 'Failed to synthesize voice preview.');
    } finally {
      setIsPlayingAudio(false);
    }
  }

  function renderStatusBadge(h: ServiceHealth) {
    if (h.status === 'testing') {
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '3px 10px',
            borderRadius: '999px',
            fontSize: '12px',
            fontWeight: 600,
            background: 'var(--blue-soft)',
            color: 'var(--blue)',
          }}
        >
          <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: 'var(--blue)', animation: 'pulse 1.2s infinite ease-in-out' }} />
          Pinging…
        </span>
      );
    }
    if (h.status === 'active') {
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '3px 10px',
            borderRadius: '999px',
            fontSize: '12px',
            fontWeight: 600,
            background: 'var(--ok-soft)',
            color: 'var(--ok)',
          }}
        >
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--ok)' }} />
          Active ({h.latencyMs}ms)
        </span>
      );
    }
    if (h.status === 'offline') {
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '3px 10px',
            borderRadius: '999px',
            fontSize: '12px',
            fontWeight: 600,
            background: 'var(--bad-soft)',
            color: 'var(--bad)',
          }}
        >
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--bad)' }} />
          Offline / Failed
        </span>
      );
    }
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '3px 10px',
          borderRadius: '999px',
          fontSize: '12px',
          fontWeight: 500,
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          color: 'var(--muted)',
        }}
      >
        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--faint)' }} />
        Untested
      </span>
    );
  }

  return (
    <div className="admin-page">
      <AdminPageHeader
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'AI & LLM Providers' },
        ]}
        icon="🤖"
        title="Tenant AI & Multi-LLM Provider Configuration"
        badge="Multi-Model Governance"
        description={`Configure enterprise AI models, conversation persona LLMs, scoring engines, and speech providers for organization ${user?.tenantSlug ?? user?.tenantId ?? 'Current'}.`}
      />

      {/* Super Admin Tenant Switcher */}
      {isSuperAdmin && (
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(30, 58, 138, 0.08) 100%)',
            border: '1px solid rgba(37, 99, 235, 0.2)',
            borderRadius: '12px',
            padding: '14px 20px',
            marginBottom: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '20px' }}>🏢</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: '13.5px', color: '#1e3a8a' }}>
                Super Admin Scope: Active Organization LLM Settings
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                Configure and test LLM providers, speech models, and API keys for the selected enterprise tenant.
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#1e3a8a' }}>Organization:</label>
            <select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              className="admin-form__select"
              style={{ minWidth: '220px', padding: '8px 12px', fontSize: '13px', borderColor: '#93c5fd', background: '#fff' }}
            >
              {tenantsList.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.slug || t.id})
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {isLoading ? (
        <div style={{ background: '#FFFFFF', border: '1px solid var(--line, #E2E8F0)', borderRadius: '14px', padding: '60px 24px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
          <div className="dashboard-spinner" style={{ margin: '0 auto 16px' }} />
          <p style={{ color: 'var(--muted, #64748B)', fontSize: '14px', fontWeight: 500, margin: 0 }}>
            Loading AI multi-model providers & voice credentials…
          </p>
        </div>
      ) : (
        <>
          {feedbackMessage && (
            <div className={`admin-alert ${feedbackMessage.type === 'success' ? 'admin-alert--success' : 'admin-alert--error'}`}>
              {feedbackMessage.text}
            </div>
          )}

          {/* Top Live Service Diagnostics & Health Matrix */}
      <div
        style={{
          background: 'linear-gradient(135deg, var(--white) 0%, #F9FAFD 100%)',
          border: '1px solid var(--line)',
          borderRadius: '14px',
          padding: '20px 24px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(7, 29, 69, 0.04)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '14px', marginBottom: '16px' }}>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--navy)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>⚡</span> Live Service Diagnostics & Health Matrix
            </h2>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: 'var(--muted)' }}>
              Monitor and test real-time connectivity, latency, and operational health across all active AI services.
            </p>
          </div>
          <button
            type="button"
            onClick={pingAllServices}
            disabled={isPingingAll}
            className="admin-btn admin-btn--primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 16px', fontSize: '13px' }}
          >
            {isPingingAll ? (
              <>
                <span className="dashboard-spinner" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />
                Pinging All Services…
              </>
            ) : (
              <>⚡ Ping All Services (Full Health Check)</>
            )}
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
          {/* Persona LLM Card */}
          <div style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '10px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--navy)' }}>🤖 Persona LLM</span>
              {renderStatusBadge(health.persona)}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--muted)', display: 'flex', justifyContent: 'space-between' }}>
              <span>{personaProvider.toUpperCase()} ({personaModel})</span>
              {health.persona.testedAt && <span>{health.persona.testedAt}</span>}
            </div>
            {health.persona.message && (
              <div style={{ fontSize: '11px', color: health.persona.status === 'offline' ? 'var(--bad)' : 'var(--muted)', marginTop: '2px', lineHeight: 1.3 }}>
                {health.persona.message}
              </div>
            )}
            <button
              type="button"
              onClick={() => testService('persona')}
              disabled={health.persona.status === 'testing' || isPingingAll}
              className="admin-btn admin-btn--secondary admin-btn--sm"
              style={{ marginTop: 'auto', alignSelf: 'flex-start', fontSize: '11px', padding: '4px 10px' }}
            >
              {health.persona.status === 'testing' ? 'Testing…' : 'Ping Persona'}
            </button>
          </div>

          {/* Evaluation LLM Card */}
          <div style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '10px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--navy)' }}>📊 Evaluation LLM</span>
              {renderStatusBadge(health.eval)}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--muted)', display: 'flex', justifyContent: 'space-between' }}>
              <span>{evaluationProvider.toUpperCase()} ({evaluationModel})</span>
              {health.eval.testedAt && <span>{health.eval.testedAt}</span>}
            </div>
            {health.eval.message && (
              <div style={{ fontSize: '11px', color: health.eval.status === 'offline' ? 'var(--bad)' : 'var(--muted)', marginTop: '2px', lineHeight: 1.3 }}>
                {health.eval.message}
              </div>
            )}
            <button
              type="button"
              onClick={() => testService('eval')}
              disabled={health.eval.status === 'testing' || isPingingAll}
              className="admin-btn admin-btn--secondary admin-btn--sm"
              style={{ marginTop: 'auto', alignSelf: 'flex-start', fontSize: '11px', padding: '4px 10px' }}
            >
              {health.eval.status === 'testing' ? 'Testing…' : 'Ping Evaluation'}
            </button>
          </div>

          {/* STT Card */}
          <div style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '10px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--navy)' }}>🎙️ Speech-to-Text (STT)</span>
              {renderStatusBadge(health.stt)}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--muted)', display: 'flex', justifyContent: 'space-between' }}>
              <span>{sttProvider.toUpperCase()} ({sttLanguage})</span>
              {health.stt.testedAt && <span>{health.stt.testedAt}</span>}
            </div>
            {health.stt.message && (
              <div style={{ fontSize: '11px', color: health.stt.status === 'offline' ? 'var(--bad)' : 'var(--muted)', marginTop: '2px', lineHeight: 1.3 }}>
                {health.stt.message}
              </div>
            )}
            <button
              type="button"
              onClick={() => testService('stt')}
              disabled={health.stt.status === 'testing' || isPingingAll}
              className="admin-btn admin-btn--secondary admin-btn--sm"
              style={{ marginTop: 'auto', alignSelf: 'flex-start', fontSize: '11px', padding: '4px 10px' }}
            >
              {health.stt.status === 'testing' ? 'Testing…' : 'Ping STT'}
            </button>
          </div>

          {/* TTS Card */}
          <div style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '10px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--navy)' }}>🔊 Voice Synthesis (TTS)</span>
              {renderStatusBadge(health.tts)}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--muted)', display: 'flex', justifyContent: 'space-between' }}>
              <span>{ttsProvider.toUpperCase()} ({ttsLanguage})</span>
              {health.tts.testedAt && <span>{health.tts.testedAt}</span>}
            </div>
            {health.tts.message && (
              <div style={{ fontSize: '11px', color: health.tts.status === 'offline' ? 'var(--bad)' : 'var(--muted)', marginTop: '2px', lineHeight: 1.3 }}>
                {health.tts.message}
              </div>
            )}
            <button
              type="button"
              onClick={() => testService('tts')}
              disabled={health.tts.status === 'testing' || isPingingAll}
              className="admin-btn admin-btn--secondary admin-btn--sm"
              style={{ marginTop: 'auto', alignSelf: 'flex-start', fontSize: '11px', padding: '4px 10px' }}
            >
              {health.tts.status === 'testing' ? 'Testing…' : 'Ping TTS'}
            </button>
          </div>
        </div>
      </div>

      <form onSubmit={handleSave} style={{ display: 'grid', gap: '20px' }}>
        {/* Section 1: Live Simulation Persona LLM */}
        <div className="admin-section" style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '12px', padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', borderBottom: '1px solid var(--line)', paddingBottom: '12px' }}>
            <div>
              <h2 className="admin-section__title" style={{ fontSize: '16px' }}>
                🤖 1. Live Simulation Persona LLM
              </h2>
              <p style={{ margin: '2px 0 0', fontSize: '13px', color: 'var(--muted)' }}>
                Drives the interactive voice/chat dialogue with the learner in real time.
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {renderStatusBadge(health.persona)}
              <button
                type="button"
                onClick={() => testService('persona')}
                disabled={health.persona.status === 'testing' || isPingingAll}
                className="admin-btn admin-btn--secondary admin-btn--sm"
              >
                {health.persona.status === 'testing' ? 'Testing…' : '⚡ Test Connection'}
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginTop: '12px' }}>
            <div className="admin-form__group">
              <label className="admin-form__label">Provider</label>
              <select
                value={personaProvider}
                onChange={(e) => setPersonaProvider(e.target.value)}
                className="admin-form__select"
              >
                <option value="openai">OpenAI</option>
                <option value="gemini">Google Gemini</option>
                <option value="anthropic">Anthropic Claude</option>
                <option value="ollama">Local Ollama</option>
                <option value="mock">Deterministic Mock (Test)</option>
              </select>
            </div>
            <div className="admin-form__group">
              <label className="admin-form__label">Model Name</label>
              <input
                type="text"
                value={personaModel}
                onChange={(e) => setPersonaModel(e.target.value)}
                className="admin-form__input"
              />
            </div>
            <div className="admin-form__group">
              <label className="admin-form__label">
                Temperature ({personaTemperature})
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={personaTemperature}
                onChange={(e) => setPersonaTemperature(Number(e.target.value))}
                style={{ height: '40px', accentColor: 'var(--blue)' }}
              />
            </div>
          </div>
        </div>

        {/* Section 2: Authoritative Evaluation Engine LLM */}
        <div className="admin-section" style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '12px', padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', borderBottom: '1px solid var(--line)', paddingBottom: '12px' }}>
            <div>
              <h2 className="admin-section__title" style={{ fontSize: '16px' }}>
                📊 2. Evaluation & Rubric Scoring LLM
              </h2>
              <p style={{ margin: '2px 0 0', fontSize: '13px', color: 'var(--muted)' }}>
                Evaluates transcripts against rubric dimensions, generating scores and coaching notes.
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {renderStatusBadge(health.eval)}
              <button
                type="button"
                onClick={() => testService('eval')}
                disabled={health.eval.status === 'testing' || isPingingAll}
                className="admin-btn admin-btn--secondary admin-btn--sm"
              >
                {health.eval.status === 'testing' ? 'Testing…' : '⚡ Test Connection'}
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginTop: '12px' }}>
            <div className="admin-form__group">
              <label className="admin-form__label">Provider</label>
              <select
                value={evaluationProvider}
                onChange={(e) => setEvaluationProvider(e.target.value)}
                className="admin-form__select"
              >
                <option value="openai">OpenAI</option>
                <option value="gemini">Google Gemini</option>
                <option value="anthropic">Anthropic Claude</option>
                <option value="mock">Deterministic Evaluator</option>
              </select>
            </div>
            <div className="admin-form__group">
              <label className="admin-form__label">Model Name</label>
              <input
                type="text"
                value={evaluationModel}
                onChange={(e) => setEvaluationModel(e.target.value)}
                className="admin-form__input"
              />
            </div>
            <div className="admin-form__group">
              <label className="admin-form__label">
                Temperature ({evaluationTemperature})
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={evaluationTemperature}
                onChange={(e) => setEvaluationTemperature(Number(e.target.value))}
                style={{ height: '40px', accentColor: 'var(--blue)' }}
              />
            </div>
          </div>
        </div>

        {/* Section 3: Speech Audio STT & TTS Providers */}
        <div className="admin-section" style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '12px', padding: '24px' }}>
          <div style={{ borderBottom: '1px solid var(--line)', paddingBottom: '12px' }}>
            <h2 className="admin-section__title" style={{ fontSize: '16px' }}>
              🎙️ 3. Speech Recognition & Voice Synthesis (STT / TTS)
            </h2>
            <p style={{ margin: '2px 0 0', fontSize: '13px', color: 'var(--muted)' }}>
              Provides real-time speech transcription and natural vocal synthesis with live diagnostic testing.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginTop: '16px' }}>
            {/* STT Configuration */}
            <div style={{ display: 'grid', gap: '14px', background: 'var(--surface)', padding: '16px', borderRadius: '10px', border: '1px solid var(--line)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>Speech-to-Text (STT)</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {renderStatusBadge(health.stt)}
                  <button
                    type="button"
                    onClick={() => testService('stt')}
                    disabled={health.stt.status === 'testing' || isPingingAll}
                    className="admin-btn admin-btn--secondary admin-btn--sm"
                    style={{ fontSize: '11px', padding: '3px 8px' }}
                  >
                    {health.stt.status === 'testing' ? 'Testing…' : '⚡ Ping STT'}
                  </button>
                </div>
              </div>
              <div className="admin-form__group">
                <label className="admin-form__label">Provider</label>
                <select
                  value={sttProvider}
                  onChange={(e) => setSttProvider(e.target.value)}
                  className="admin-form__select"
                >
                  <option value="google">Google Cloud STT</option>
                  <option value="sarvam">Sarvam AI (Indian Languages)</option>
                  <option value="whisper">OpenAI Whisper</option>
                  <option value="mock">Web Speech API Fallback</option>
                </select>
              </div>
              <div className="admin-form__group">
                <label className="admin-form__label">Default STT Language</label>
                <input
                  type="text"
                  value={sttLanguage}
                  onChange={(e) => setSttLanguage(e.target.value)}
                  className="admin-form__input"
                />
              </div>
            </div>

            {/* TTS Configuration */}
            <div style={{ display: 'grid', gap: '14px', background: 'var(--surface)', padding: '16px', borderRadius: '10px', border: '1px solid var(--line)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>Text-to-Speech (TTS)</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {renderStatusBadge(health.tts)}
                  <button
                    type="button"
                    onClick={() => testService('tts')}
                    disabled={health.tts.status === 'testing' || isPingingAll}
                    className="admin-btn admin-btn--secondary admin-btn--sm"
                    style={{ fontSize: '11px', padding: '3px 8px' }}
                  >
                    {health.tts.status === 'testing' ? 'Testing…' : '⚡ Ping TTS'}
                  </button>
                </div>
              </div>
              <div className="admin-form__group">
                <label className="admin-form__label">Provider</label>
                <select
                  value={ttsProvider}
                  onChange={(e) => setTtsProvider(e.target.value)}
                  className="admin-form__select"
                >
                  <option value="google">Google Cloud TTS</option>
                  <option value="openai">OpenAI Audio Speech TTS (Premium)</option>
                  <option value="azure">Microsoft Azure Neural</option>
                  <option value="sarvam">Sarvam AI TTS</option>
                  <option value="mock">Browser Synthesis Fallback</option>
                </select>
                {ttsProvider === 'openai' && (
                  <p style={{ margin: '6px 0 0', fontSize: '11.5px', color: 'var(--muted)' }}>
                    💡 <em>OpenAI Audio TTS voices include: alloy, echo, fable, onyx, nova, shimmer. Note: incur OpenAI Audio Speech API charges.</em>
                  </p>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="admin-form__group">
                  <label className="admin-form__label">Voice Identifier</label>
                  <input
                    type="text"
                    value={ttsVoiceId}
                    onChange={(e) => setTtsVoiceId(e.target.value)}
                    className="admin-form__input"
                  />
                </div>
                <div className="admin-form__group">
                  <label className="admin-form__label">Language</label>
                  <input
                    type="text"
                    value={ttsLanguage}
                    onChange={(e) => setTtsLanguage(e.target.value)}
                    className="admin-form__input"
                  />
                </div>
              </div>

              {/* Interactive Audio Voice Preview Tester */}
              <div style={{ borderTop: '1px dashed var(--line)', paddingTop: '12px', marginTop: '4px' }}>
                <label className="admin-form__label" style={{ fontSize: '12px', marginBottom: '6px' }}>
                  🔊 Live Voice Synthesis Audio Preview
                </label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={previewText}
                    onChange={(e) => setPreviewText(e.target.value)}
                    placeholder="Enter preview sentence…"
                    className="admin-form__input"
                    style={{ fontSize: '12px', padding: '6px 10px' }}
                  />
                  <button
                    type="button"
                    onClick={handlePlayPreview}
                    disabled={isPlayingAudio}
                    className="admin-btn admin-btn--secondary"
                    style={{ whiteSpace: 'nowrap', fontSize: '12px', padding: '6px 12px' }}
                  >
                    {isPlayingAudio ? 'Generating…' : '▶ Play Audio'}
                  </button>
                </div>
                {audioError && (
                  <div style={{ fontSize: '11px', color: 'var(--bad)', marginTop: '4px' }}>
                    {audioError}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Section 4: Secure API Key Vault */}
        <div className="admin-section" style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '12px', padding: '24px' }}>
          <div style={{ borderBottom: '1px solid var(--line)', paddingBottom: '12px' }}>
            <h2 className="admin-section__title" style={{ fontSize: '16px' }}>
              🔒 4. Enterprise API Key Vault (Encrypted & Masked)
            </h2>
            <p style={{ margin: '2px 0 0', fontSize: '13px', color: 'var(--muted)' }}>
              Active keys are securely encrypted at rest. Test individual key validity directly with dedicated ping buttons.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginTop: '16px' }}>
            {/* OpenAI Key */}
            <div className="admin-form__group" style={{ background: 'var(--surface)', padding: '14px', borderRadius: '10px', border: '1px solid var(--line)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label className="admin-form__label" style={{ margin: 0 }}>
                  OpenAI API Key
                </label>
                {renderStatusBadge(health.openai)}
              </div>
              {config?.keysConfigured?.openai && (
                <div style={{ fontSize: '11px', color: 'var(--ok)', marginBottom: '6px' }}>
                  ✓ Key Active: {config.keysConfigured.openaiMasked}
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="password"
                  placeholder="sk-..."
                  value={openaiKey}
                  onChange={(e) => setOpenaiKey(e.target.value)}
                  className="admin-form__input"
                />
                <button
                  type="button"
                  onClick={() => testService('openai')}
                  disabled={health.openai.status === 'testing' || isPingingAll}
                  className="admin-btn admin-btn--secondary admin-btn--sm"
                  style={{ whiteSpace: 'nowrap', padding: '0 10px', fontSize: '11px' }}
                >
                  {health.openai.status === 'testing' ? '…' : 'Ping Key'}
                </button>
              </div>
              {health.openai.message && (
                <div style={{ fontSize: '11px', color: health.openai.status === 'offline' ? 'var(--bad)' : 'var(--muted)', marginTop: '4px' }}>
                  {health.openai.message}
                </div>
              )}
            </div>

            {/* Google Cloud Key */}
            <div className="admin-form__group" style={{ background: 'var(--surface)', padding: '14px', borderRadius: '10px', border: '1px solid var(--line)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label className="admin-form__label" style={{ margin: 0 }}>
                  Google Cloud / Gemini Key
                </label>
                {renderStatusBadge(health.gemini)}
              </div>
              {config?.keysConfigured?.googleCloud && (
                <div style={{ fontSize: '11px', color: 'var(--ok)', marginBottom: '6px' }}>
                  ✓ Key Active: {config.keysConfigured.googleCloudMasked}
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="password"
                  placeholder="AIzaSy..."
                  value={googleKey}
                  onChange={(e) => setGoogleKey(e.target.value)}
                  className="admin-form__input"
                />
                <button
                  type="button"
                  onClick={() => testService('gemini')}
                  disabled={health.gemini.status === 'testing' || isPingingAll}
                  className="admin-btn admin-btn--secondary admin-btn--sm"
                  style={{ whiteSpace: 'nowrap', padding: '0 10px', fontSize: '11px' }}
                >
                  {health.gemini.status === 'testing' ? '…' : 'Ping Key'}
                </button>
              </div>
              {health.gemini.message && (
                <div style={{ fontSize: '11px', color: health.gemini.status === 'offline' ? 'var(--bad)' : 'var(--muted)', marginTop: '4px' }}>
                  {health.gemini.message}
                </div>
              )}
            </div>

            {/* Anthropic Key */}
            <div className="admin-form__group" style={{ background: 'var(--surface)', padding: '14px', borderRadius: '10px', border: '1px solid var(--line)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label className="admin-form__label" style={{ margin: 0 }}>
                  Anthropic API Key
                </label>
                {renderStatusBadge(health.anthropic)}
              </div>
              {config?.keysConfigured?.anthropic && (
                <div style={{ fontSize: '11px', color: 'var(--ok)', marginBottom: '6px' }}>
                  ✓ Key Active: {config.keysConfigured.anthropicMasked}
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="password"
                  placeholder="sk-ant-..."
                  value={anthropicKey}
                  onChange={(e) => setAnthropicKey(e.target.value)}
                  className="admin-form__input"
                />
                <button
                  type="button"
                  onClick={() => testService('anthropic')}
                  disabled={health.anthropic.status === 'testing' || isPingingAll}
                  className="admin-btn admin-btn--secondary admin-btn--sm"
                  style={{ whiteSpace: 'nowrap', padding: '0 10px', fontSize: '11px' }}
                >
                  {health.anthropic.status === 'testing' ? '…' : 'Ping Key'}
                </button>
              </div>
              {health.anthropic.message && (
                <div style={{ fontSize: '11px', color: health.anthropic.status === 'offline' ? 'var(--bad)' : 'var(--muted)', marginTop: '4px' }}>
                  {health.anthropic.message}
                </div>
              )}
            </div>

            {/* Ollama URL */}
            <div className="admin-form__group" style={{ background: 'var(--surface)', padding: '14px', borderRadius: '10px', border: '1px solid var(--line)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label className="admin-form__label" style={{ margin: 0 }}>
                  Ollama Base URL (Local)
                </label>
                {renderStatusBadge(health.ollama)}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  placeholder="http://localhost:11434"
                  value={ollamaUrl}
                  onChange={(e) => setOllamaUrl(e.target.value)}
                  className="admin-form__input"
                />
                <button
                  type="button"
                  onClick={() => testService('ollama')}
                  disabled={health.ollama.status === 'testing' || isPingingAll}
                  className="admin-btn admin-btn--secondary admin-btn--sm"
                  style={{ whiteSpace: 'nowrap', padding: '0 10px', fontSize: '11px' }}
                >
                  {health.ollama.status === 'testing' ? '…' : 'Ping'}
                </button>
              </div>
              {health.ollama.message && (
                <div style={{ fontSize: '11px', color: health.ollama.status === 'offline' ? 'var(--bad)' : 'var(--muted)', marginTop: '4px' }}>
                  {health.ollama.message}
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button
            type="submit"
            disabled={isSaving}
            className="admin-btn admin-btn--primary"
          >
            {isSaving ? 'Saving Configuration…' : 'Save AI Configuration'}
          </button>
        </div>
      </form>
        </>
      )}
    </div>
  );
}

